
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Upload, ScanText, Copy, Save } from 'lucide-react';
import { scanDocument } from '@/services/ocrService';
import { useLanguage } from '@/contexts/LanguageContext';

const DocumentScanner: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { language, t } = useLanguage();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file) {
      // Check if the file is an image
      if (!file.type.match('image.*')) {
        toast.error(t('scanner.invalidFile'));
        return;
      }
      
      // Create a preview URL for the image
      const fileUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(fileUrl);
      setExtractedText('');
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    
    if (file) {
      // Check if the file is an image
      if (!file.type.match('image.*')) {
        toast.error(t('scanner.invalidFile'));
        return;
      }
      
      // Create a preview URL for the image
      const fileUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(fileUrl);
      setExtractedText('');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleScan = async () => {
    if (!selectedFile) return;
    
    setIsProcessing(true);
    
    try {
      const result = await scanDocument(selectedFile, language);
      
      if (result.success) {
        setExtractedText(result.text);
        toast.success(t('scanner.success'));
      } else {
        toast.error(result.error || t('scanner.error'));
      }
    } catch (error) {
      console.error('Error during scanning:', error);
      toast.error(t('scanner.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyText = () => {
    if (extractedText) {
      navigator.clipboard.writeText(extractedText)
        .then(() => toast.success(t('message.copied')))
        .catch(() => toast.error(t('message.error')));
    }
  };

  const handleSaveToDocuments = () => {
    try {
      if (!extractedText.trim()) {
        toast.error(t('scanner.noText'));
        return;
      }
      
      // Create a document title from the first line or a default
      const firstLine = extractedText.split('\n')[0] || (language === 'ur' ? 'اسکین شدہ دستاویز' : 'Scanned Document');
      const title = firstLine.length > 30 ? `${firstLine.substring(0, 30)}...` : firstLine;
      
      // Create a document object
      const document = {
        id: Date.now().toString(),
        content: extractedText,
        date: new Date().toISOString(),
        title: title
      };
      
      // Get existing documents or initialize empty array
      const savedDocs = JSON.parse(localStorage.getItem('adaaltBotDocs') || '[]');
      
      // Add new document and save back to localStorage
      savedDocs.push(document);
      localStorage.setItem('adaaltBotDocs', JSON.stringify(savedDocs));
      
      toast.success(t('message.saved'));
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(t('message.error'));
    }
  };

  const clearScan = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setExtractedText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4">
      <Card className="p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-center">
          {t('scanner.title')}
        </h2>
        
        <div 
          className={`border-2 border-dashed rounded-lg p-6 mb-6 text-center ${previewUrl ? 'border-green-500' : 'border-gray-300'} cursor-pointer hover:border-primary transition-colors`}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          {previewUrl ? (
            <div className="flex flex-col items-center">
              <img 
                src={previewUrl} 
                alt="Document preview" 
                className="max-h-64 mb-4 rounded-md object-contain" 
              />
              <p className="text-sm text-muted-foreground">
                {selectedFile?.name}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8">
              <Upload className="h-12 w-12 text-gray-400 mb-2" />
              <p className="mb-1 font-medium">
                {t('scanner.dropzone')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('scanner.formats')}
              </p>
            </div>
          )}
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
            aria-label="Upload document"
          />
        </div>

        <div className="flex flex-wrap gap-2 justify-center mb-6">
          <Button
            onClick={handleScan}
            disabled={!selectedFile || isProcessing}
            className="flex items-center gap-2"
          >
            {isProcessing ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <ScanText size={16} />
            )}
            {t('scanner.scan')}
          </Button>
          
          {selectedFile && (
            <Button
              onClick={clearScan}
              variant="outline"
            >
              {t('scanner.clear')}
            </Button>
          )}
        </div>

        {extractedText && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-medium">
                {t('scanner.results')}
              </h3>
              <div className="flex gap-2">
                <Button
                  onClick={handleCopyText}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Copy size={16} />
                  {t('result.copy')}
                </Button>
                <Button
                  onClick={handleSaveToDocuments}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Save size={16} />
                  {t('result.save')}
                </Button>
              </div>
            </div>
            
            <Textarea
              value={extractedText}
              onChange={(e) => setExtractedText(e.target.value)}
              className={`min-h-[200px] text-lg p-4 ${language === 'ur' ? "text-right font-urdu" : "text-left"}`}
              placeholder={t('scanner.placeholder')}
              dir={language === 'ur' ? 'rtl' : 'ltr'}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default DocumentScanner;
